// Frühere Fassungen eines Inhalts — lesen und zurückholen.
//
// ⚠️ Warum es diesen Baustein überhaupt gibt: `RevisionService` schreibt seit jeher bei JEDER
// Änderung eine Fassung mit — acht Schreibpfade im Backend füttern die Tabelle
// (`ContentService`, `AudioService`, `VideoService`, `MindBookService`, je einmal für Ändern und
// einmal für Löschen). Gelesen hat sie im CMS bis hierher NIEMAND. Zwei Endpunkte standen fertig
// da, ohne eine einzige Oberfläche davor: eine versehentliche Änderung war damit zwar
// aufgezeichnet, aber nicht rückholbar.
//
// ── Was diese Runde bewusst NICHT tut ──────────────────────────────────────────────────────────
// **Kein Feld-Vergleich zweier Fassungen.** Das Backend könnte ihn (`RevisionService.diffRevision`
// rechnet ihn aus), aber es gibt keine Route darauf, und die Ansicht dafür ist ein eigener
// Schnitt. Das ist auch die Grenze, die die Branche zieht: Strapi liefert seinen Versionsverlauf
// als Liste plus „Wiederherstellen" aus, ohne Gegenüberstellung. Eine halbe Vergleichsansicht wäre
// schlechter als keine — sie verspräche eine Aussage über das, was sich geändert hat, und stützte
// sie auf `changedFields`, also auf die Schlüssel des Anfrage-Körpers und nicht auf einen echten
// Wertevergleich.
//
// ── Die Bedeutung einer Zeile, und warum die Beschriftung daran hängt ──────────────────────────
// ⚠️ Am Backend nachgemessen: `createRevision` läuft VOR dem Schreiben und bekommt die Zeile, wie
// sie bis dahin war. Eine Fassung hält also den Stand fest, der VOR der jeweiligen Änderung galt.
// Die Tabelle sagt das hin („Stand vor dieser Änderung"), statt „Fassung 3" wie einen
// Speicherpunkt aussehen zu lassen — sonst holte die Nutzerin beim Klick etwas anderes zurück, als
// sie erwartet, und zwar genau um einen Schritt daneben.
//
// ── Aufklappen: gebaut, weil es das Muster im Projekt NICHT gibt ───────────────────────────────
// ⚠️ Nachgemessen: `aria-expanded` und `aria-controls` kommen in `src/` an keiner einzigen Stelle
// vor. Die einzige vorhandene Auf-/Zuklapp-Mechanik (`TopicCategoryManagement.toggleExpand`)
// tauscht nur ein Symbol — für eine Vorlesehilfe ist dort nicht erkennbar, dass ein Bereich
// aufgeht, geschweige denn welcher. Diese Handkopie wird hier NICHT fortgesetzt: der Knopf trägt
// `aria-expanded` und `aria-controls`, der Bereich seine Kennung und einen Namen.
//
// Warum überhaupt zugeklappt und nicht einfach immer sichtbar: die Fassungen stehen UNTER einem
// langen Formular und sind Nebeninformation. Vor allem aber kostet die Liste einen eigenen
// Netzabruf — bei „immer offen" liefe der bei jedem Klick auf „Bearbeiten" mit, auch wenn niemand
// hinsieht. Geladen wird deshalb beim ersten Aufklappen.
//
// ── Zustand von außen, nicht von hier ──────────────────────────────────────────────────────────
// `frage` und `melde` kommen als Eigenschaften herein, statt dass dieser Baustein sich eigene
// Haken zieht. Grund: die Seite rendert `<Rueckfrage>` und `<Benachrichtigung>` bereits ÜBER ihrer
// Formular/Listen-Weiche — ausdrücklich dort, damit eine Meldung den Zweigwechsel überlebt
// (Begründung in `ContentManagement.tsx`). Ein zweites Paar hier hieße zwei Dialog-Quellen im
// selben Baum und eine Erfolgsmeldung, die mit dem Bauteil verschwindet, das sie ausgelöst hat.
// Dasselbe Muster wie die `notify`-Eigenschaft der Formulare.
import React, { useCallback, useId, useState } from 'react';
import { ArrowUturnLeftIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { apiService } from '../../services/api';
import { userService } from '../../services/userService';
import { getErrorMessage } from '../../utils/errors';
import { datumZeit } from '../../utils/datum';
import { AENDERUNGSART_ANZEIGE, FELD_ANZEIGE, type ContentRevision } from '../../types/revision';
import type { BenachrichtigungsTyp } from '../ui/Benachrichtigung';
import type { RückfrageWunsch } from '../ui/Rueckfrage';

interface VersionslisteProps {
  /** Der Inhalt, dessen Fassungen gezeigt werden. Nur im Bearbeiten-Zweig vorhanden. */
  contentId: string;
  /** Titel des Inhalts — steht in der Rückfrage, damit sie nennt, WORAN sie etwas ändert. */
  contentTitel: string;
  /** Aus `useRueckfrage` der Seite. Siehe Begründung im Dateikopf. */
  frage: (wunsch: RückfrageWunsch) => Promise<boolean>;
  /** Aus `useBenachrichtigung` der Seite. */
  melde: (type: BenachrichtigungsTyp, message: string) => void;
  /** Läuft nach erfolgreichem Wiederherstellen — die Seite räumt Formular und Liste auf. */
  onWiederhergestellt: () => void;
}

/** Steht in einer Zelle, für die es keine Angabe gibt. Wie `KEIN_DATUM`, nur ohne Datumsbezug. */
const OHNE_ANGABE = '–';

/** Der Wert, den das Backend einsetzt, wenn kein Konto am Vorgang hängt. */
const SYSTEM_KENNUNG = 'system';

/**
 * Abzeichen für die Änderungsart — dieselbe Bauform wie `StatusBadge`, aber mit eigener Karte.
 *
 * ⚠️ Bewusst NICHT `StatusBadge` mit einem zweiten Wertevorrat gefüttert: dessen Karte
 * (`STATUS_ANZEIGE`) beschreibt den INHALTS-Status. `UPDATE`/`STATUS_CHANGE`/`DELETE` stehen dort
 * nicht, ein Aufruf fiele also auf den Rückfallzweig und zeigte den rohen Wert. Zwei Begriffe in
 * einer Karte wären zudem eine Zeitbombe für den Tag, an dem sich ein Name überschneidet.
 *
 * Nicht exportiert, weil es außerhalb dieser Datei keinen Verbraucher gibt — dieselbe Regel, die
 * `knip` für Werte fährt.
 */
const ArtAbzeichen: React.FC<{ art: string }> = ({ art }) => {
  const eintrag = AENDERUNGSART_ANZEIGE[art];
  // Unbekannte Art: roher Wert statt leerer Fläche — sichtbar falsch schlägt unsichtbar weg.
  if (!eintrag) return <span className="badge badge-secondary">{art}</span>;
  return <span className={`badge ${eintrag.ton}`}>{eintrag.text}</span>;
};

/**
 * Aus einem Profil den Namen bauen, der in der Tabelle steht.
 * Vor- und Nachname können im Backend leere Zeichenketten sein; dann trägt der Benutzername.
 */
const nameVon = (profil: { username: string; firstName: string; lastName: string }): string => {
  const voll = `${profil.firstName ?? ''} ${profil.lastName ?? ''}`.trim();
  return voll || profil.username || OHNE_ANGABE;
};

/**
 * Die Kennungen einer Fassungsliste zu Namen auflösen.
 *
 * ⚠️ `Promise.allSettled` und nicht `Promise.all`: ein einziges nicht auflösbares Konto — ein
 * deaktiviertes (`/public` filtert auf `isActive: true`), ein zwischenzeitlich gelöschtes — würde
 * sonst die Namen ALLER Zeilen mitreißen. Ein Fehlschlag je Kennung bedeutet genau eines: dieser
 * Name ist nicht bekannt. Angezeigt wird dann „Unbekannt", nie die rohe Kennung; eine cuid in der
 * Spalte „Geändert von" ist für einen Menschen wertlos und sieht zugleich aus, als stünde dort
 * eine Information.
 *
 * `'system'` wird gar nicht erst abgefragt — es ist keine Benutzer-Kennung, sondern der Rückfall,
 * den `ContentService.restoreContentVersion` einsetzt, wenn kein Konto am Vorgang hängt.
 */
const namenAufloesen = async (revisionen: ContentRevision[]): Promise<Record<string, string>> => {
  const kennungen = [...new Set(revisionen.map((r) => r.changedBy))].filter(
    (kennung) => kennung && kennung !== SYSTEM_KENNUNG,
  );
  const ergebnisse = await Promise.allSettled(kennungen.map((k) => userService.getPublicProfile(k)));

  const namen: Record<string, string> = {};
  ergebnisse.forEach((ergebnis, i) => {
    if (ergebnis.status === 'fulfilled' && ergebnis.value) {
      namen[kennungen[i]] = nameVon(ergebnis.value);
    }
  });
  return namen;
};

export const Versionsliste: React.FC<VersionslisteProps> = ({
  contentId,
  contentTitel,
  frage,
  melde,
  onWiederhergestellt,
}) => {
  const bereichId = useId();
  const knopfId = useId();

  const [offen, setOffen] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [geladen, setGeladen] = useState(false);
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false);
  const [revisionen, setRevisionen] = useState<ContentRevision[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [namen, setNamen] = useState<Record<string, string>>({});
  const [laufendeId, setLaufendeId] = useState<string | null>(null);

  const lade = useCallback(async () => {
    setLaedt(true);
    setFehlgeschlagen(false);
    try {
      const antwort = await apiService.getContentVersions(contentId);
      setRevisionen(antwort.revisions);
      setGesamt(antwort.pagination.total);
      // Die Namen werden NACH der Liste gesetzt: die Tabelle steht damit sofort, und die Spalte
      // „Geändert von" füllt sich nach. Ein gemeinsames Warten hielte die ganze Liste auf, obwohl
      // der Name ihr unwichtigster Teil ist.
      setNamen(await namenAufloesen(antwort.revisions));
      setGeladen(true);
    } catch (fehler) {
      console.error('Fassungen konnten nicht geladen werden:', fehler);
      // ⚠️ Ohne Meldung sähe ein gescheiterter Abruf wie „keine Fassungen vorhanden" aus — also
      // wie die Aussage, dass es nichts zurückzuholen gibt. Genau die Verwechslung, gegen die der
      // Fehlerpfad-Wächter der Seiten geschrieben ist.
      melde('error', `Fassungen konnten nicht geladen werden: ${getErrorMessage(fehler)}`);
      setFehlgeschlagen(true);
    } finally {
      setLaedt(false);
    }
  }, [contentId, melde]);

  const umschalten = () => {
    const jetztOffen = !offen;
    setOffen(jetztOffen);
    // Beim ersten Aufklappen laden — und nach einem Fehlschlag beim nächsten erneut versuchen.
    if (jetztOffen && !geladen && !laedt) void lade();
  };

  const wiederherstellen = async (revision: ContentRevision) => {
    if (
      !(await frage({
        titel: 'Fassung wiederherstellen?',
        text:
          `Fassung ${revision.version} hält den Stand von „${contentTitel}" fest, der vor der ` +
          `Änderung vom ${datumZeit(revision.changedAt)} galt. Dieser Stand wird zum aktuellen.\n\n` +
          'Der jetzige Stand geht dabei NICHT verloren: er wird vorher automatisch als neue ' +
          'Fassung gesichert und lässt sich genauso zurückholen.\n\n' +
          'Der Inhalt landet danach im Entwurf und ist nicht mehr öffentlich sichtbar. ' +
          'Veröffentlichen bleibt ein eigener, bewusster Schritt.',
        bestätigenText: 'Wiederherstellen',
        // ⚠️ KEIN `gefahr`. Die rote Kennzeichnung ist in `Rueckfrage.tsx` für Löschungen und
        // Abschaltungen reserviert, und zwar mit Begründung: eine Farbe, die überall Alarm
        // schlägt, wird überall überhört. Hier geht nichts verloren — der aktuelle Stand wird
        // vorher gesichert, der Schritt ist mit einer weiteren Wiederherstellung umkehrbar. Was
        // folgenreich ist (der Inhalt geht offline), steht im Text und nicht in einer Farbe.
      }))
    ) {
      return;
    }

    setLaufendeId(revision.id);
    let erfolg = false;
    try {
      await apiService.restoreContentVersion(contentId, revision.id);
      erfolg = true;
    } catch (fehler) {
      console.error('Wiederherstellen fehlgeschlagen:', fehler);
      melde('error', `Wiederherstellen fehlgeschlagen: ${getErrorMessage(fehler)}`);
    } finally {
      setLaufendeId(null);
    }

    // Erst NACH dem Aufräumen des Ladezustands: `onWiederhergestellt` baut dieses Bauteil ab
    // (die Seite schließt das Formular), ein `setLaufendeId` danach liefe ins Leere.
    if (erfolg) {
      melde(
        'success',
        `Fassung ${revision.version} wiederhergestellt. Der Inhalt steht jetzt im Entwurf.`,
      );
      onWiederhergestellt();
    }
  };

  const Pfeil = offen ? ChevronDownIcon : ChevronRightIcon;

  return (
    <div className="card">
      <button
        type="button"
        id={knopfId}
        onClick={umschalten}
        aria-expanded={offen}
        aria-controls={bereichId}
        className="flex w-full cursor-pointer items-center gap-3 p-6 text-left"
      >
        <Pfeil className="h-5 w-5 flex-shrink-0 text-secondary-400" aria-hidden="true" />
        <span className="text-lg font-medium text-secondary-900">Frühere Fassungen</span>
        {geladen && (
          <span className="badge badge-secondary">
            {gesamt === 1 ? '1 Fassung' : `${gesamt} Fassungen`}
          </span>
        )}
      </button>

      {/* ⚠️ Der Bereich wird beim Zuklappen aus dem Baum genommen, statt nur ausgeblendet zu
          werden. `hidden` mit `display: none` wäre für Vorlesehilfen gleichwertig, hielte aber
          eine Tabelle mit bis zu fünfzig Zeilen im Dokument — ohne jeden Nutzen, solange niemand
          hinsieht. Der geladene Zustand bleibt im Haken erhalten, ein zweites Aufklappen löst
          deshalb keinen zweiten Abruf aus. */}
      {offen && (
        <div
          id={bereichId}
          role="region"
          aria-labelledby={knopfId}
          className="border-t border-secondary-200"
        >
          {laedt ? (
            <div className="space-y-3 p-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="skeleton h-10 w-full"></div>
              ))}
            </div>
          ) : fehlgeschlagen ? (
            <div className="p-6 text-center">
              <p className="mx-auto mb-4 max-w-prose text-secondary-500">
                Die Fassungen konnten nicht geladen werden. Das heißt NICHT, dass es keine gibt.
              </p>
              <button type="button" onClick={() => void lade()} className="btn btn-outline cursor-pointer">
                Erneut versuchen
              </button>
            </div>
          ) : revisionen.length === 0 ? (
            <div className="p-6 text-center">
              <p className="mx-auto max-w-prose text-secondary-500">
                Für diesen Inhalt ist noch keine frühere Fassung gespeichert. Eine Fassung
                entsteht bei jeder Änderung — die erste also, sobald dieser Inhalt das nächste Mal
                gespeichert wird.
              </p>
            </div>
          ) : (
            <>
              <p className="px-6 pt-4 text-sm text-secondary-500">
                Jede Zeile hält den Stand fest, der VOR der jeweiligen Änderung galt.
                „Wiederherstellen" holt genau diesen Stand zurück — der aktuelle wird dabei als
                neue Fassung gesichert, und der Inhalt landet im Entwurf.
              </p>
              {/* ⚠️ Nur bei echtem Überhang, und ohne Blätter-Leiste: der Endpunkt ist
                  serverseitig auf Seite 1 mit 50 Einträgen festgenagelt (siehe
                  `apiService.getContentVersions`). Knöpfe anzubieten, die nichts bewirken
                  können, wäre dieselbe Attrappe wie der frühere „Filter anwenden"-Knopf der
                  Inhalte-Liste. Also lieber hinsagen, dass hier nicht alles steht. */}
              {gesamt > revisionen.length && (
                <p className="px-6 pt-2 text-sm text-secondary-500">
                  Angezeigt werden die {revisionen.length} jüngsten von insgesamt {gesamt}{' '}
                  Fassungen. Ältere sind über diese Ansicht nicht erreichbar.
                </p>
              )}
              <div className="mt-4 overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Fassung</th>
                      <th>Geändert am</th>
                      <th>Geändert von</th>
                      <th>Art</th>
                      <th>Geänderte Felder</th>
                      <th>Beschreibung</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revisionen.map((revision) => (
                      <tr key={revision.id}>
                        <td className="font-medium">{revision.version}</td>
                        <td>{datumZeit(revision.changedAt)}</td>
                        <td>
                          {revision.changedBy === SYSTEM_KENNUNG
                            ? 'System'
                            : (namen[revision.changedBy] ?? 'Unbekannt')}
                        </td>
                        <td>
                          <ArtAbzeichen art={revision.changeType} />
                        </td>
                        {/* ⚠️ `whitespace-normal` am inneren `div`, nicht an der `td`: dort verlöre es gegen
                            `.table td` (Spezifität 0,1,1 gegen 0,1,0) und die Spalte bräche nie um —
                            so stand es hier bis 2026-10-05. Wache: `listenBreite.guard.test.ts`. */}
                        <td>
                          <div className="whitespace-normal">
                            {revision.changedFields.length > 0
                              ? revision.changedFields
                                  .map((feld) => FELD_ANZEIGE[feld] ?? feld)
                                  .join(' · ')
                              : OHNE_ANGABE}
                          </div>
                        </td>
                        <td>
                          <div className="whitespace-normal">{revision.description || OHNE_ANGABE}</div>
                        </td>
                        <td>
                          {/* ⚠️ `aria-label` mit der Fassungsnummer, obwohl der sichtbare Text
                              „Wiederherstellen" lautet: in einer Tabelle mit zehn Zeilen hießen
                              sonst zehn Knöpfe gleich, und wer die Knopfliste einer Vorlesehilfe
                              durchgeht, hört zehnmal dasselbe ohne Zeilenbezug. Der sichtbare Text
                              steckt im Namen (WCAG 2.5.3), die Sprachbedienung greift also
                              weiterhin auf „Wiederherstellen". */}
                          <button
                            type="button"
                            onClick={() => void wiederherstellen(revision)}
                            disabled={laufendeId !== null}
                            className="btn btn-outline btn-sm cursor-pointer"
                            aria-label={`Fassung ${revision.version} wiederherstellen`}
                          >
                            <ArrowUturnLeftIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                            Wiederherstellen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
